import _ from 'lodash';
import moment from 'moment';
import { RRule } from 'rrule';
import { v4 as uuid } from 'uuid';
import { EVENT_SOURCE, STATUS_LIST, TASK_PRIORITY, ASSET_TYPES } from './enums';
import { parseJSONStringToObject } from './UtilityFunctions';
import ApiService from '../service/apiService';
import { TASK_ATTRIBUTES_NEEDED, CURRENT_WORKFLOW_VERSION } from './constants';
import * as RRuleFunctions from './RRuleFunctions';
import * as DateUtils from './DateUtils';
import { RRULE_FREQUENCY_MAP } from '../components/taskView/TaskViewPage/ManageTaskForm/lib/constants';

export function isTaskParent(task) {
  return task.workflowVersion && !task.parentTaskId;
}

export function isTaskChild(task) {
  return task.workflowVersion && task.parentTaskId;
}

export function doesTaskBelongToAssigneeId(task, assigneeId) {
  if (!task.assigneeIds) return false;
  return (
    task.assigneeIds
      .filter((assignee) => !assignee.isDeleted)
      .map((assignee) => assignee.assigneeId)
      .indexOf(assigneeId) > -1
  );
}

export function rruleStringTorruleObj(task) {
  const rruleString = _.get(task, 'schedule.rrule');
  const overwriteEditedOccurrences = _.get(task, 'schedule.overwriteEditedOccurrences', true);
  const allowPileups = _.get(task, 'schedule.allowPileups', true);
  const displayUptoDays = _.get(task, 'schedule.displayUptoDays', 15);
  if (rruleString) {
    const rruleObj = RRule.parseString(rruleString);
    // delete rruleObj.byhour;
    // delete rruleObj.byminute;
    // delete rruleObj.bysecond;
    // delete rruleObj.wkst;
    const dateStartAtCurrentTZ = moment(rruleObj.dtstart).date();
    const dateStartAtUTC = moment(rruleObj.dtstart).utc().date();
    if (dateStartAtCurrentTZ > dateStartAtUTC) {
      if (rruleObj.freq === RRule.WEEKLY) {
        rruleObj.byweekday = rruleObj.byweekday.map((weekday) =>
          RRuleFunctions.getRRuleWeekDay(RRuleFunctions.getDayNoForRRuleWeekDay(weekday) + 1)
        );
      } else if (rruleObj.freq === RRule.MONTHLY) {
        rruleObj.bymonthday = (rruleObj.bymonthday % 30) + 1;
      }
    } else if (dateStartAtCurrentTZ < dateStartAtUTC) {
      if (rruleObj.freq === RRule.WEEKLY) {
        rruleObj.byweekday = rruleObj.byweekday.map((weekday) =>
          RRuleFunctions.getRRuleWeekDay(RRuleFunctions.getDayNoForRRuleWeekDay(weekday) + 6)
        );
      } else if (rruleObj.freq === RRule.MONTHLY) {
        rruleObj.bymonthday = ((rruleObj.bymonthday + 28) % 30) + 1;
      }
    }
    return {
      frequencytype: RRULE_FREQUENCY_MAP[rruleObj.freq],
      dailyinterval: rruleObj.freq === RRule.DAILY ? rruleObj.interval : 1,
      monthlyinterval: rruleObj.freq === RRule.MONTHLY ? rruleObj.interval : 1,
      byhour: rruleObj.byhour,
      byminute: rruleObj.byminute,
      bysecond: rruleObj.bysecond,
      wkst: rruleObj.wkst,
      byweekday: _.get(rruleObj, 'byweekday', []),
      bymonthday: rruleObj.bymonthday,
      startdate: DateUtils.convertDateToYYYYMMDDFormat(rruleObj.dtstart),
      enddate: DateUtils.convertDateToYYYYMMDDFormat(rruleObj.until),
      overwriteEditedOccurrences,
      allowPileups,
      displayUptoDays,
    };
  }
  return {
    frequencytype: RRULE_FREQUENCY_MAP[RRule.DAILY],
    dailyinterval: 1,
    monthlyinterval: 1,
    byweekday: [],
    bymonthday: 1,
    startdate: DateUtils.convertDateToYYYYMMDDFormat(moment()),
    enddate: '2030-12-30',
    overwriteEditedOccurrences,
    allowPileups,
    displayUptoDays,
  };
}

export function rruleObjtoStringwithCurrentTimeasStartTime(rruleObj) {
  const endDate = _.get(rruleObj, 'enddate');
  const frequencyType = _.get(rruleObj, 'frequencytype');

  const dtstart = DateUtils.setTimeToDate(
    new Date(`${moment().format('YYYY-MM-DD')}T00:00`),
    moment().endOf('h').format('HH:MM')
  );
  const dateStartAtCurrentTZ = moment(dtstart).date();
  const dateStartAtUTC = moment(dtstart).utc().date();
  let byweekday = _.get(rruleObj, 'byweekday');
  let bymonthday = _.get(rruleObj, 'bymonthday');
  if (dateStartAtCurrentTZ > dateStartAtUTC) {
    if (frequencyType === RRULE_FREQUENCY_MAP[RRule.WEEKLY]) {
      byweekday = byweekday.map((weekday) =>
        RRuleFunctions.getRRuleWeekDay(RRuleFunctions.getDayNoForRRuleWeekDay(weekday) + 6)
      );
    } else if (frequencyType === RRULE_FREQUENCY_MAP[RRule.MONTHLY]) {
      bymonthday = ((parseInt(bymonthday) + 28) % 30) + 1;
    }
  } else if (dateStartAtCurrentTZ < dateStartAtUTC) {
    if (frequencyType === RRULE_FREQUENCY_MAP[RRule.WEEKLY]) {
      byweekday = byweekday.map((weekday) =>
        RRuleFunctions.getRRuleWeekDay(RRuleFunctions.getDayNoForRRuleWeekDay(weekday) + 1)
      );
    } else if (frequencyType === RRULE_FREQUENCY_MAP[RRule.MONTHLY]) {
      bymonthday = (parseInt(bymonthday) % 30) + 1;
    }
  } else {
    bymonthday = parseInt(bymonthday);
  }
  const INVERTED_RRULE_FREQUENCY_MAP = _.invert(RRULE_FREQUENCY_MAP);
  const rrule = {
    dtstart,
    until: DateUtils.setTimeToDate(new Date(`${endDate}T00:00`), moment().format('HH:MM')),
    interval:
      // eslint-disable-next-line no-nested-ternary
      frequencyType === RRULE_FREQUENCY_MAP[RRule.DAILY]
        ? _.get(rruleObj, 'dailyinterval')
        : frequencyType === RRULE_FREQUENCY_MAP[RRule.MONTHLY]
          ? _.get(rruleObj, 'monthlyinterval')
          : null,
    byweekday: frequencyType === RRULE_FREQUENCY_MAP[RRule.WEEKLY] ? byweekday : null,
    bymonthday: frequencyType === RRULE_FREQUENCY_MAP[RRule.MONTHLY] ? bymonthday : null,
    freq: INVERTED_RRULE_FREQUENCY_MAP[frequencyType],
  };
  return RRule.optionsToString(new RRule(rrule).options);
}

export async function dependencyChildTasksCheck(parentTaskId, stask, allTasks, taskViewAction) {
  taskViewAction.startPageLoadingTask();
  const childTaskFromReducer = allTasks.filter((task) => task.parentTaskId === parentTaskId);
  const childTaskDict = _.keyBy(childTaskFromReducer, 'id');
  const tasksObject = await ApiService.getChildTasksByParentTaskId(parentTaskId);
  const returnValue = [];
  if (tasksObject) {
    let tasksToBeUpdated = tasksObject.childTasks ?? [];

    tasksToBeUpdated = tasksToBeUpdated
      .filter((t) => !childTaskDict[t.id])
      .map((t) => {
        const task = _.pick(t, TASK_ATTRIBUTES_NEEDED);
        task.assignee = JSON.stringify(task.assignee || {});
        task.data = JSON.stringify(task.data || {});
        return task;
      });
    if (tasksObject.parentTask) {
      returnValue.push(...[tasksObject.parentTask, ...tasksToBeUpdated, ...childTaskFromReducer]);
    }
  }
  taskViewAction.stopPageLoadingTask();
  if (_.isArray(stask)) {
    const childTasksIds = returnValue
      .map((e) => e.dependsOn)
      .flatMap((e) => e)
      .filter((e) => e);

    return stask.map((e) => childTasksIds.includes(e));
  }
  return returnValue
    .map((e) => e.dependsOn)
    .flatMap((e) => e)
    .filter((e) => e)
    .includes(stask.id);
}

export function doesTaskBelongToAnyAssigneeIds(task, assigneeIds) {
  for (const assigneeId of assigneeIds) {
    if (doesTaskBelongToAssigneeId(task, assigneeId)) {
      return true;
    }
  }
  return false;
}

export function isTaskSeriesOfRecurrence(task) {
  return !task.sourceRecurringTaskId && task.eventSource === EVENT_SOURCE.RECURRING;
}

export function isTaskRegular(task) {
  return !isTaskParent(task) && !isTaskSeriesOfRecurrence(task);
}

export function getSequenceNumberForTaskAndAssigneeId(task, assigneeId) {
  const assignee =
    task.assigneeIds && task.assigneeIds.filter((a) => !a.isDeleted).find((a) => a.assigneeId === assigneeId);
  return assignee && assignee.sequence;
}

export function isTaskAwaitingAssigneeAcceptance(task) {
  return (
    task &&
    task.taskType &&
    task.taskType.needAcceptance &&
    task.assignedToUserId &&
    task.status === STATUS_LIST.NOT_READY.key &&
    !task.isAcceptedByAssignee
  );
}

export function isTaskOccurrenceOfRecurringAndMaterialized(task) {
  return task.sourceRecurringTaskId && !task.isVirtual && task.originalRecurrenceDate;
}

export function isTaskOccurrenceOfRecurringAndVirtual(task) {
  return task.sourceRecurringTaskId && task.isVirtual;
}

export function isTaskOccurrenceOfRecurring(task) {
  return !!task.sourceRecurringTaskId;
}

export function isTaskRecurring(task) {
  return task.eventSource === EVENT_SOURCE.RECURRING;
}

export function isTaskDeleted(taskId, allTasks) {
  return _.findIndex(allTasks, { id: taskId }) === -1;
}

export function isSeriesTaskDeleted(task, allTasks) {
  return isTaskDeleted(task.sourceRecurringTaskId, allTasks);
}

export function isUserCapableForTask(user, taskType) {
  const certificatesNeeded = taskType.certificates ? taskType.certificates.map((c) => c.id) : [];
  const skillLevelNeeded = taskType.skillLevel ? taskType.skillLevel.value : false;

  if (!certificatesNeeded.length && !skillLevelNeeded) {
    return true;
  }
  const userCertificates = user.certificates
    ? user.certificates
        .filter((c) => !c.isExpirable || (c.expiryDate && moment().isSameOrBefore(c.expiryDate, 'day')))
        .map((c) => c.id)
    : [];
  const userSkillLevel = user.skillLevel ? user.skillLevel.value : false;

  const hasSkillLevel = skillLevelNeeded ? userSkillLevel && userSkillLevel >= skillLevelNeeded : true;
  const hasCertificates = _.intersection(certificatesNeeded, userCertificates).length === certificatesNeeded.length;

  return hasSkillLevel && hasCertificates;
}

export async function getAllowedAssigneeForTask({ asset, taskType }) {
  const areaIds = asset.areas?.filter((a) => !a.isDeleted).map((a) => a.areaId);
  const teamIds = [taskType.teamId];
  const userResponse = await ApiService.getUsersByTeamIdsAndAreaIds(
    teamIds,
    areaIds,
    asset.fieldId || taskType.fieldId
  );
  if (!_.get(userResponse, 'data.rpcService.success')) {
    throw userResponse;
  }
  let assignees = _.get(userResponse, 'data.rpcService.output');
  assignees = JSON.parse(assignees);
  assignees = assignees.filter((assignee) => isUserCapableForTask(assignee, taskType));
  return assignees;
}

export async function getAllowedAssigneeAndEquipmentOptionsForTemplateItem({ asset, taskType }) {
  const allowedAssignee = await getAllowedAssigneeForTask({
    asset,
    taskType,
  });

  let assigneeOptions = allowedAssignee;
  let equipmentOptions = [];

  if (taskType.needEquipment) {
    assigneeOptions = allowedAssignee.filter((item) => !item.isEquipmentResource);
    equipmentOptions = allowedAssignee.filter((item) => item.isEquipmentResource);
  }

  assigneeOptions = assigneeOptions.map((a) => {
    return {
      key: a.id,
      value: a.id,
      text: `${a.firstName} ${a.lastName}`,
      originalobject: a,
    };
  });

  equipmentOptions = equipmentOptions.map((a) => {
    return {
      key: a.id,
      value: a.id,
      text: `${a.firstName} ${a.lastName}`,
      originalobject: a,
    };
  });

  return {
    assigneeOptions,
    equipmentOptions,
  };
}

function compareAssignees(assignee1, assignee2) {
  return (
    assignee1.assigneeId === assignee2.assigneeId &&
    assignee1.sequence === assignee2.sequence &&
    !assignee1.isDeleted === !assignee2.isDeleted
  );
}

export function getTasksFormedOutofRecords(record, originalTask, schedulerInstance) {
  const { id: taskId, startDate, duration: durationInSec } = record;
  const assignments = schedulerInstance.eventStore.getActualAssignmentsForEvent(record);
  // have randomly put sequence for the time being, will sequence it properly
  assignments.forEach((a) => {
    a.sequence = a.sequence || a.data.sequence || _.random(1, 10, false);
  });

  const assigneeIds = assignments.map((a) => {
    const user = schedulerInstance.resourceStore.getById(a.resourceId);
    const vendorId = user && user.data && user.data.vendorId;
    return {
      assigneeId: a.resourceId,
      sequence: a.sequence,
      isDeleted: false,
      vendorId,
    };
  });
  const assigneeIdsDict = _.keyBy(assigneeIds, 'assigneeId');

  _.get(originalTask, 'assigneeIds', []).forEach((a) => {
    const { isAccepted = false, acceptedOn = false } = a;
    if (!assigneeIdsDict[a.assigneeId]) {
      assigneeIds.push({ ...a, sequence: null, isDeleted: true });
    } else {
      assigneeIdsDict[a.assigneeId].isAccepted = isAccepted;
      if (isAccepted && acceptedOn) {
        assigneeIdsDict[a.assigneeId].acceptedOn = acceptedOn;
      }
    }
  });
  const taskFormedOutOfRecord = {
    id: taskId,
    startTime: startDate.toISOString(),
    duration: _.floor(durationInSec / 60),
    assigneeIds,
    status: record.status,
    priority: record.priority,
  };
  return taskFormedOutOfRecord;
}

/**
 * this compares the task in terms of scheduling, it checks the duration, startTime and non deleted assigneeIds of task.
 * @param task1
 * @param task2
 * @returns {boolean}
 */
export function areTasksSameWrtScheduling(task1, task2) {
  let areTasksSame =
    task1.id === task2.id &&
    task1.duration === task2.duration &&
    moment(task1.startTime).isSame(moment(task2.startTime), 'minutes') &&
    task1.status === task2.status &&
    task1.priority === task2.priority;
  if (areTasksSame) {
    const assigneeIds1 = task1.assigneeIds.filter((a) => !a.isDeleted);
    const assigneeIds2 = task2.assigneeIds.filter((a) => !a.isDeleted);
    areTasksSame = _.xorWith(assigneeIds1, assigneeIds2, compareAssignees).length === 0;
  }
  return areTasksSame;
}

function getAssetObject(task, oldTask, assetDict, stopForSelectedAsset) {
  let asset;
  if (task.asset) {
    return parseJSONStringToObject(task.asset);
  }
  // checking if assetId didn't change, then oldTask's asset will be used
  if (oldTask && oldTask.assetId === task.assetId) {
    asset = oldTask.asset;
  }
  if (asset) return asset;

  asset = _.clone(_.get(assetDict, `${task.assetId}.originalobject`));
  if (asset.type === 'stop') {
    asset.stop = _.clone(asset);
  } else {
    asset.stop = _.clone(stopForSelectedAsset);
  }
  return asset;
}

function getTaskTypeObject(task, oldTask, taskTypeOptions) {
  let taskType;
  // checking if task type id has not been changed, then old task's task type will be used
  if (oldTask && task.taskTypeId === oldTask.taskTypeId) {
    taskType = task.taskType;
  }
  // otherwise fetching taskType object from taskTypeOptions
  if (taskType) return taskType;
  taskType = _.get(
    taskTypeOptions.items.find((item) => item.key === task.taskTypeId),
    'originalobject'
  );
  return taskType;
}

function areAssigneesSameInBothTasks(task1, task2) {
  const assigneeIds1 = task1.assigneeIds.filter((a) => !a.isDeleted).map((a) => a.assigneeId);
  const assigneeIds2 = task2.assigneeIds.filter((a) => !a.isDeleted).map((a) => a.assigneeId);
  return _.xor(assigneeIds1, assigneeIds2).length === 0;
}

function getAssigneeObject(task, oldTask, assigneeDict) {
  let assignee = null; // checking if assigneeIds have not been chaned, then same assignee object wil be used
  if (oldTask && areAssigneesSameInBothTasks(task, oldTask)) {
    assignee = oldTask.assignee;
  }
  if (assignee) return assignee;
  // otherwise, assignee object will be created using picklist options
  assignee = {};
  task.assigneeIds
    .filter((a) => !a.isDeleted)
    .forEach((item) => {
      if (assigneeDict[item.assigneeId]) {
        assignee[item.assigneeId] = assigneeDict[item.assigneeId].originalobject;
      } else {
        console.error(`Did not find assignee option for assignee id ${item.assigneeId} for task id ${task.id}`);
      }
    });
  return JSON.stringify(assignee);
}

export function updateNormalizedTaskProperties(task, oldTask, picklistOptions, tenantId, stopForSelectedAsset) {
  const assigneeCompleteDict = _.keyBy(picklistOptions.assigneeOptions.items, 'key');
  const assetCompleteDict = _.keyBy(picklistOptions?.assetOptions?.items || [], 'key');
  const stopCompleteDict = _.keyBy(picklistOptions?.stopOptions?.items || [], 'key');
  task.asset = getAssetObject(task, oldTask, { ...assetCompleteDict, ...stopCompleteDict }, stopForSelectedAsset);
  task.taskType = getTaskTypeObject(task, oldTask, picklistOptions.taskTypeOptions);
  task.assignee = getAssigneeObject(task, oldTask, assigneeCompleteDict);
  task.tenantId = tenantId;
  return task;
}

export const availableAssigneeForTheTask = ({ users, areas, taskTypes, task }) => {
  const areaIdsOfTask = (_.get(task, 'asset.areas') ?? [])
    .filter((areaObject) => !areaObject.isDeleted)
    .map((a) => a.areaId);
  const taskTypeId = task?.taskTypeId;
  const taskType = taskTypes?.find((tType) => tType.id === taskTypeId);
  if (!taskType) return [];
  const areasOfTask = areas.filter((area) => areaIdsOfTask.includes(area.id));
  const resourceIds = areasOfTask.flatMap((area) => {
    const teamRelatedToTaskType = area.teams
      ?.filter((team) => !team.isDeleted)
      .find((team) => team.teamId === taskType.teamId);
    const resourcesInTeams = teamRelatedToTaskType?.resources?.filter((r) => !r.isDeleted).map((r) => r.userId);
    return resourcesInTeams ?? [];
  });
  const assignees = users.filter((user) => resourceIds.includes(user.id) && isUserCapableForTask(user, taskType));
  return assignees;
};

export function doesTaskBelongToSelectedArea(task, selectedAreas) {
  const taskAreaIds = (_.get(task, 'asset.areas') || []).filter((area) => !area.isDeleted).map((area) => area.areaId);
  const selectedAreaThatAreInTaskArea = selectedAreas.filter((area) => taskAreaIds.includes(area.id));
  if (!_.size(selectedAreaThatAreInTaskArea)) {
    return false;
  }
  const teamIds = _.uniq(
    _.flatMap(selectedAreaThatAreInTaskArea || [], (e) => {
      return (e.teams || []).filter((el) => el.isDeleted !== true).map((t) => t.teamId);
    })
  );
  const taskTeamId = task.taskType.teamId;
  return teamIds.includes(taskTeamId);
}

export function doesTaskTypeBelongToSelectedGroup(taskType, selectedTaskTypeGroups = []) {
  const selectedTaskTypeGroupIds = selectedTaskTypeGroups.map((item) => item.id);
  const { groups = [] } = taskType || {};
  const groupIds = (groups ?? []).map((item) => item.id);

  let groupFound = false;

  // atleast one of the groupIds should be partof selectedTaskTypeGroupIds
  for (const id of groupIds) {
    if (selectedTaskTypeGroupIds.indexOf(id) !== -1) {
      groupFound = true;
      break;
    }
  }
  return groupFound;
}
export function doesTaskBelongToSelectedTaskTypeGroups(task, selectedTaskTypeGroups = []) {
  if (!selectedTaskTypeGroups.length) {
    return true;
  }
  return doesTaskTypeBelongToSelectedGroup(task?.taskType, selectedTaskTypeGroups);
}

export function filterTasksEligibleForScheduler(tasks) {
  return tasks.filter((t) => {
    let flag = [
      STATUS_LIST.NOT_READY.key,
      STATUS_LIST.OPEN.key,
      STATUS_LIST.CHANGE_REQUEST.key,
      STATUS_LIST.RESOLVED.key,
    ].includes(t.status);
    if (flag) {
      flag = !isTaskSeriesOfRecurrence(t);
    }
    if (flag) {
      flag = !isTaskParent(t);
    }
    return flag;
  });
}

export function findTimeForReminder(task, dynamicStartTime) {
  const defaultRemainderTime = { days: 0, hours: 0, minutes: 0 };
  const enableReminder = _.get(task, 'taskType.reminder.enableReminder', false);
  if (!enableReminder) {
    return null;
  }
  const { days = 0, hours = 0, minutes = 0 } = _.get(task, 'taskType.reminder.reminderTime') || defaultRemainderTime;
  // convert days hours to minutes
  const reminderTime = days * 1440 + hours * 60 + minutes;
  const currDate = moment();
  const startTime = dynamicStartTime || task.startTime;
  const diffTime = moment(startTime).diff(currDate, 'minutes');
  if (diffTime <= 0) {
    return '0d 0h 0m';
  }
  if (diffTime < reminderTime) {
    const t = Math.abs(diffTime);
    return `${parseInt(t / 24 / 60)}d ${parseInt((t / 60) % 24)}h ${parseInt(t % 60)}m`;
  }
  return null;
}

export function shouldShowForLoggedInVendor(assignees, loggedinUser, draftState) {
  if (!loggedinUser.vendorId) {
    return true;
  }
  // hide draft state tasks from the vendor
  if (draftState) {
    return false;
  }
  return (assignees || [])
    .filter((assignee) => !assignee.isDeleted)
    .some((assigneeId) => assigneeId.vendorId === loggedinUser.vendorId);
}

export const getPriorityList = (
  basePriority = TASK_PRIORITY.LOW.key,
  maxPriority = TASK_PRIORITY.CRITICAL.key,
  tasktypePriorityAlias
) => {
  const reqPriority = [];
  const priorityListArr = [
    TASK_PRIORITY.LOW.key,
    TASK_PRIORITY.MEDIUM.key,
    TASK_PRIORITY.HIGH.key,
    TASK_PRIORITY.CRITICAL.key,
  ];
  let foundMin = false;
  const priorityAlias = parseJSONStringToObject(tasktypePriorityAlias);

  for (const priority of priorityListArr) {
    const index = priorityListArr.indexOf(priority);
    if (priority === basePriority) {
      // reqPriority[index] = priority;
      foundMin = true;
    }

    if (foundMin) {
      reqPriority[index] = priority;
    }
    if (priority === maxPriority && foundMin) {
      if (basePriority !== maxPriority) {
        reqPriority[index] = priority;
      }
      foundMin = false;
      break;
    }
  }

  return reqPriority
    .map((priority) => {
      return {
        text: _.get(priorityAlias, `values.${priority}`) || priority,
        value: priority,
        key: priority,
      };
    })
    .filter((val) => val.text);
};

export const getPriorityTemplateSpecs = (priorityTemplate) => {
  const desc = {
    basePriority: '',
    maxPriority: '',
    timeToNextPriority: '',
  };
  if (priorityTemplate) {
    const splitVal = priorityTemplate.split('-');
    [desc.basePriority, desc.maxPriority, desc.timeToNextPriority] = splitVal;
    desc.timeToNextPriority = parseInt(desc.timeToNextPriority);
  }
  return desc;
};

export const getTimeToNextPriorityOptions = () => {
  const timeToNextPriorityList = [1, 2, 3, 5, 10];
  return timeToNextPriorityList.map((priority) => {
    return {
      text: priority,
      key: priority,
      value: priority,
    };
  });
};

export const taskStringPriorityConditioning = (priorityTask, TASK_PRIORITY_KEYED, taskId) => {
  let conditionString = '';
  if (!_.isEmpty(priorityTask)) {
    if (
      !TASK_PRIORITY_KEYED[priorityTask] &&
      (!TASK_PRIORITY_KEYED[priorityTask].backgroundColor ||
        !TASK_PRIORITY_KEYED[priorityTask].borderColor ||
        !TASK_PRIORITY_KEYED[priorityTask].text)
    ) {
      console.error('priority is incorrect', taskId, priorityTask);
    }
    conditionString = priorityTask
      .split('')
      .map((e, i) => (i === 0 ? e.toUpperCase() : e.toLowerCase()))
      .join('');
    if (
      TASK_PRIORITY_KEYED[conditionString].backgroundColor &&
      TASK_PRIORITY_KEYED[conditionString].borderColor &&
      TASK_PRIORITY_KEYED[conditionString].text
    ) {
      return conditionString;
    }
    console.error('priority is incorrect after string conditioning', taskId, priorityTask);

    return conditionString;
  }
  console.error('priority is empty', taskId, priorityTask);
  return conditionString;
};

export const taskStringStatusConditioning = (statusTask, STATUS_LIST_TEXT_KEYED, taskId) => {
  let returnString = '';
  if (!_.isEmpty(statusTask)) {
    if (!STATUS_LIST_TEXT_KEYED[statusTask] || !STATUS_LIST_TEXT_KEYED[statusTask].text) {
      console.error('status is incorrect', taskId, statusTask);
    }
    returnString = statusTask.toLowerCase();
    if (STATUS_LIST_TEXT_KEYED && STATUS_LIST_TEXT_KEYED[statusTask] && STATUS_LIST_TEXT_KEYED[statusTask].text) {
      returnString = STATUS_LIST_TEXT_KEYED[statusTask].text;
      return returnString;
    }
    console.error('status is incorrect after string conditioning', taskId, statusTask);
    return returnString;
  }

  console.error('status is empty for', taskId, statusTask);
  return returnString;
};

export const getUpdatedAssigneeOnAcceptingTask = (assigneeIdsArg, assigneeId, operatorId) => {
  const assigneeIds = _.clone(assigneeIdsArg);
  const ifAssigneePresent = assigneeIds.find((a) => a.assigneeId === assigneeId);
  if (!ifAssigneePresent) {
    const objToAdd = {};
    objToAdd.assigneeId = assigneeId;
    if (operatorId) {
      objToAdd.operatorId = assigneeId;
    }
    assigneeIds.push(objToAdd);
  }
  if (operatorId) {
    const ifOperatorPesent = assigneeIds.find((a) => a.assigneeId === operatorId);
    if (!ifOperatorPesent) {
      assigneeIds.push({ assigneeId: operatorId });
    }
  }
  const reqAssignee = assigneeIds.find((assignee) => assignee.assigneeId === assigneeId);
  reqAssignee.isAccepted = true;
  reqAssignee.acceptedOn = new Date().toISOString();
  reqAssignee.isDeleted = false;
  const updatedAssigneeIds = [reqAssignee];
  assigneeIds.forEach((a) => {
    if (a.assigneeId && a.assigneeId !== reqAssignee.assigneeId) {
      let isDeletedFlag = true;
      if (operatorId) {
        // eslint-disable-next-line no-unneeded-ternary
        isDeletedFlag = a.assigneeId === operatorId ? false : true;
      }
      updatedAssigneeIds.push(_.omit({ ...a, isDeleted: isDeletedFlag }, ['isAccepted', 'acceptedOn']));
    }
  });
  return updatedAssigneeIds;
};

export const getOperatorIdAndEquipmentId = (assigneeIds) => {
  let operatorId = '';
  let equipmentId = '';
  assigneeIds = assigneeIds.filter((a) => !a.isDeleted);
  const equipment = assigneeIds.find((a) => a.operatorId) || assigneeIds[0];
  if (equipment) {
    equipmentId = equipment.assigneeId;
    const operator = assigneeIds.find((a) => a.assigneeId === equipment.operatorId);
    if (operator) {
      operatorId = operator.assigneeId;
    }
  }
  return { operatorId, equipmentId };
};

export const getEquipmentIdAndAssigneeIdsToEvaluate = (assigneeIds = [], needEquipment = false) => {
  let assigneeIdsToEvaluate = [];
  let equipmentIdToEvaluate = '';
  if (needEquipment) {
    const { operatorId, equipmentId } = getOperatorIdAndEquipmentId(assigneeIds);
    equipmentIdToEvaluate = equipmentId;
    if (operatorId) {
      assigneeIdsToEvaluate.push(operatorId);
    }
  } else {
    assigneeIdsToEvaluate = assigneeIds.filter((a) => !a.isDeleted).map((item) => item.assigneeId);
  }
  return { assigneeIdsToEvaluate, equipmentIdToEvaluate };
};

export const isUnassignAllowed = (task) => {
  const mandatoryAssignee = _.get(task, 'taskType.mandatoryAssignee', false) || false;
  if (!mandatoryAssignee) {
    return true;
  }
  const needEquipment = _.get(task, 'taskType.needEquipment', false) || false;
  if (needEquipment && mandatoryAssignee) {
    return false;
  }
  const filteredAssigneeIds = (task?.assigneeIds || []).filter((assignee) => !assignee.isDeleted);
  // mandatoryAssignee and filteredAssignees length is >0 and not 1 then unassign is not allowed
  return filteredAssigneeIds?.length && filteredAssigneeIds?.length !== 1;
};

export function mergeDropDownAssigneeWithTaskAssignee({
  dropdownIds, // selected ids from the dropdown..["a","b"]
  assigneeIds, // existing tasks assigneeIds.. [{assigneeId : 'a', isDeleted : false}, {assigneeId : 'c', isDeleted : false, vendorId: 'cVendor'}, {assigneeId : 'd', isDeleted : true}]
  openToNotReady = false,
  needEquipment = false,
  dropdownIdsAssigneeMap, // { a: {id : 'a'}, b : {id : 'b'}}
}) {
  const existingDeletedAssigneeIdObjects = assigneeIds.filter((assigneeIdObj) => assigneeIdObj.isDeleted);
  const existingAssigneeIds = assigneeIds.filter((assigneeIdObj) => !assigneeIdObj.isDeleted);
  const existingAssigneeIdMap = _.keyBy(existingAssigneeIds, 'assigneeId');
  const formvalAssigneeIds = dropdownIds.filter((val) => val);
  let assigneeIdObjects = formvalAssigneeIds.map((assigneeId) => {
    const obj = {
      assigneeId,
      isDeleted: false,
      sequence: _.get(existingAssigneeIdMap, `${assigneeId}.sequence`, null),
      vendorId: _.get(dropdownIdsAssigneeMap, `${assigneeId}.originalobject.vendorId`, null),
    };
    if (needEquipment && _.get(dropdownIdsAssigneeMap, `${assigneeId}.originalobject.isEquipmentResource`, null)) {
      const operatorId = formvalAssigneeIds.find((id) => id !== assigneeId);
      obj.operatorId = operatorId;
    }
    if (_.get(existingAssigneeIdMap, `${assigneeId}.isAccepted`, false) && !openToNotReady) {
      obj.isAccepted = _.get(existingAssigneeIdMap, `${assigneeId}.isAccepted`, false);
      obj.acceptedOn = _.get(existingAssigneeIdMap, `${assigneeId}.acceptedOn`);
    }
    return obj;
  });
  const deletedAssigneeIds = _.difference(
    existingAssigneeIds.map((assigneeIdObj) => assigneeIdObj.assigneeId),
    dropdownIds
  );
  // deleted assigneeIds will be in existingAssigneeIdMap
  const deletedAssigneeObjects = deletedAssigneeIds.map((assigneeId) => ({
    assigneeId,
    isDeleted: true,
    sequence: null,
    vendorId: _.get(existingAssigneeIdMap, `${assigneeId}.vendorId`, null),
  }));
  assigneeIdObjects = _.uniqBy(
    [...assigneeIdObjects, ...deletedAssigneeObjects, ...existingDeletedAssigneeIdObjects],
    'assigneeId'
  );
  // assigneeIdObjects would be [{assigneeId : 'a', isDeleted : false},
  // {assigneeId : 'b', isDeleted : false}, {assigneeId : 'c', isDeleted : true, vendorId: 'cVendor'}, {assigneeId : 'd', isDeleted : true}]
  return assigneeIdObjects;
}

export function getAssetCriteriaRequestObj(info) {
  const typesEvaluated = (info.types || []).length ? info.types : Object.values(ASSET_TYPES).map((item) => item.value);

  let areaIdsEvaluated = [];

  if (info.locationIds?.length) {
    areaIdsEvaluated = info.allAreaIds || [];
  } else {
    areaIdsEvaluated = info.areaIds || [];
  }

  return {
    areaIds: areaIdsEvaluated,
    types: typesEvaluated,
    assetIds: info.locationIds || [],
    stopIds: info.stopIds || [],
    customAttributes: info.customAttributes || {},
  };
}

export function computeWorkflowAndPreferredLocation(taskType, asset, parentTaskTemplate = []) {
  const preferredLocationData = [];
  const { id } = asset;
  let { preferredLocations } = asset;
  preferredLocations = preferredLocations || {};
  if (!taskType.isDynamicParentChild) {
    const { workflowVersions = [] } = taskType;
    const workflowSteps = _.get(workflowVersions, `[${0}].workflowSteps`) || [];
    const workflowStepsDict = _.keyBy(workflowSteps, 'workflowStepId');

    for (const step of workflowSteps) {
      const { workflowStepId, taskTypeId } = step;
      const preferredLocationTypeIdToUse =
        step.preferredLocationTypeId ||
        (step.sameLocationAs ? workflowStepsDict[step.sameLocationAs]?.preferredLocationTypeId : null);
      const assetId = preferredLocations[preferredLocationTypeIdToUse] || id;
      preferredLocationData.push({ assetId, workflowStepId, taskTypeId });
    }
  } else if (_.size(_.get(parentTaskTemplate, 'items'))) {
    const items = _.get(parentTaskTemplate, 'items');
    for (const step of items) {
      const { workflowStepId, originalItem } = step;
      preferredLocationData.push({ assetId: id, workflowStepId, taskTypeId: originalItem.taskTypeId });
    }
  }

  return preferredLocationData;
}

export const shouldCheckForAutoResolveFirstTask = ({ tt, task }) => {
  if (!task?.id && tt?.isParent && tt?.autoResolveFirstTask) {
    const workflowSteps = _.get(tt, 'workflowVersions[0].workflowSteps') || [];
    if (workflowSteps?.length) {
      const reqStep = workflowSteps[0];
      if (reqStep?.formSectionId) {
        return { autoResolveFirstTaskValidation: true, sectionToValidate: reqStep.formSectionId };
      }
    }
    return { autoResolveFirstTaskValidation: true };
  }
  return { autoResolveFirstTaskValidation: false };
};

export function getUrlQueryParams(location) {
  const urlSearchParams = new URLSearchParams(location.search);
  const queryParams = Object.fromEntries(urlSearchParams.entries());
  return queryParams;
}

export function defaultWorkflowStepInfo() {
  return {
    workflowStepId: uuid(),
    dependsOn: [],
    immediateNextTo: '',
    sameAssigneeAs: '',
    sameStartTimeAs: [],
    sameLocationAs: '',
    autoResolveDependsOn: [],
    workflowVersion: CURRENT_WORKFLOW_VERSION,
  };
}
